import { Check, Copy, Download, ExternalLink, Plus, RefreshCw, Sparkles } from 'lucide-react'
import { useMemo, useState } from 'react'
import { download } from '../../api/client'
import { useAddManualCategory, useCategories, useGenerateCategories, useTeams } from '../../api/hooks'
import type { Category, Event } from '../../api/types'
import { Badge, Button, Card, DrillLink, EmptyState, ErrorNote, Field, Input, Modal, TableSkeleton } from '../../components/ui'
import { useToast } from '../../components/ui/toast'
import { CUSTOMER_TYPE_LABEL } from '../../lib/format'

export function CategoriesTab({ event: e, isManager }: { event: Event; isManager: boolean }) {
  const cats = useCategories(e.id)
  const teams = useTeams()
  const gen = useGenerateCategories(e.id)
  const addManual = useAddManualCategory(e.id)
  const toast = useToast()
  const [manualOpen, setManualOpen] = useState(false)
  const [manual, setManual] = useState({ name: '', capacity: '' })
  const [filter, setFilter] = useState('')

  const list = cats.data ?? []
  const auto = list.filter((c) => !c.is_manual)
  const manualCats = list.filter((c) => c.is_manual)
  const activeTeams = teams.data?.filter((t) => t.is_active).length ?? 0
  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase()
    return q ? auto.filter((c) => c.name.toLowerCase().includes(q) || c.advisor_name?.toLowerCase().includes(q)) : auto
  }, [auto, filter])

  const generate = () =>
    gen.mutate(undefined, {
      onSuccess: (r) =>
        toast.push('success', r.created ? `${r.created} kategorier skapade, ${r.kept} oförändrade` : `Inga nya team – ${r.kept} kategorier behåller sina länkar${r.deactivated ? `, ${r.deactivated} inaktiverade` : ''}`),
      onError: (err) => toast.push('error', err.message),
    })

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-ink-500">
          En kategori per rådgivarteam och kundtyp, var och en med en unik anmälningslänk. Namnstandard <code className="rounded bg-line-100 px-1 py-0.5 text-xs">[TeamKod]_[Teamnamn]_[Kundtyp]</code>.
        </p>
        <div className="flex items-center gap-2">
          {list.length > 0 && (
            <Button size="sm" icon={<Download className="size-4" />} onClick={() => download(`/api/events/${e.id}/categories/export.csv`, `kategorier_${e.name}.csv`)}>
              Exportera CSV
            </Button>
          )}
          {isManager && list.length > 0 && <Button size="sm" icon={<Plus className="size-4" />} onClick={() => setManualOpen(true)}>Manuell kategori</Button>}
          {isManager && (
            <Button size="sm" variant={auto.length ? 'secondary' : 'primary'} icon={auto.length ? <RefreshCw className="size-4" /> : <Sparkles className="size-4" />} loading={gen.isPending} onClick={generate}>
              {auto.length ? 'Uppdatera från teamdata' : `Generera för ${activeTeams} team`}
            </Button>
          )}
        </div>
      </div>

      {cats.isLoading ? (
        <Card padded={false}><TableSkeleton rows={8} cols={6} /></Card>
      ) : auto.length === 0 && manualCats.length === 0 ? (
        <EmptyState
          icon={<Sparkles className="size-8" />}
          title="Inga kategorier ännu"
          description={`Systemet skapar ${activeTeams * 2} kategorier (kund + prospekt för ${activeTeams} team) med unika anmälningslänkar på under en sekund. Det som idag tar timmar i Trippus.`}
          action={isManager && <Button variant="primary" loading={gen.isPending} onClick={generate}>Generera kategorier</Button>}
        />
      ) : (
        <>
          <Card padded={false} title={<div className="flex items-center gap-3"><h3 className="text-sm font-semibold">Autogenererade</h3><Badge>{auto.length}</Badge></div>} actions={<Input placeholder="Filtrera på team eller rådgivare…" value={filter} onChange={(e) => setFilter(e.target.value)} className="h-8 w-64 text-xs" />}>
            <CategoryTable rows={filtered} />
          </Card>
          {manualCats.length > 0 && (
            <Card padded={false} title={<div className="flex items-center gap-3"><h3 className="text-sm font-semibold">Manuella</h3><Badge>{manualCats.length}</Badge></div>}>
              <CategoryTable rows={manualCats} />
            </Card>
          )}
        </>
      )}

      <Modal
        open={manualOpen}
        onClose={() => setManualOpen(false)}
        title="Lägg till manuell kategori"
        footer={
          <>
            <Button onClick={() => setManualOpen(false)}>Avbryt</Button>
            <Button
              variant="primary"
              loading={addManual.isPending}
              onClick={() =>
                addManual.mutate(
                  { name: manual.name.trim(), capacity: manual.capacity ? Number(manual.capacity) : null },
                  { onSuccess: () => { setManualOpen(false); setManual({ name: '', capacity: '' }); toast.push('success', 'Kategorin skapades') } },
                )
              }
            >
              Skapa
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <p className="text-sm text-ink-500">För gäster utanför rådgivarteamen, t.ex. VIP, styrelse eller partners. Får en egen anmälningslänk.</p>
          <Field label="Namn"><Input value={manual.name} onChange={(e) => setManual((s) => ({ ...s, name: e.target.value }))} placeholder="t.ex. VIP_Styrelse" /></Field>
          <Field label="Max antal (valfritt)"><Input type="number" min={1} value={manual.capacity} onChange={(e) => setManual((s) => ({ ...s, capacity: e.target.value }))} /></Field>
          <ErrorNote error={addManual.error} />
        </div>
      </Modal>
    </div>
  )
}

function CategoryTable({ rows }: { rows: Category[] }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[720px]">
        <thead className="table-head"><tr><th>Kategori</th><th>Rådgivare</th><th>Kundtyp</th><th className="text-right">Mottagare</th><th className="text-right">Anmälda</th><th>Anmälningslänk</th></tr></thead>
        <tbody>
          {rows.map((c) => (
            <tr key={c.id} className={`table-row ${!c.is_active ? 'opacity-50' : ''}`}>
              <td className="font-medium text-ink-900">
                <span className="font-mono text-[13px]">{c.name}</span>
                {!c.is_active && <Badge tone="warning" className="ml-2">Team borttaget</Badge>}
              </td>
              <td className="text-ink-500">{c.advisor_name ?? '–'}</td>
              <td>{c.customer_type ? <Badge tone={c.customer_type === 'KUND' ? 'success' : 'copper'}>{CUSTOMER_TYPE_LABEL[c.customer_type]}</Badge> : <Badge>Manuell</Badge>}</td>
              <td className="text-right tabular-nums text-ink-500">{c.is_manual ? '–' : c.recipients}</td>
              <td className="text-right font-semibold tabular-nums text-ink-900"><DrillLink to={`/event/${c.event_id}/deltagare?category=${c.id}`}>{c.registered}</DrillLink>{c.capacity ? <span className="font-normal text-ink-500"> / {c.capacity}</span> : ''}</td>
              <td><LinkCell url={c.registration_url} /></td>
            </tr>
          ))}
          {rows.length === 0 && <tr><td colSpan={6} className="px-3 py-6 text-center text-sm text-ink-500">Inga träffar.</td></tr>}
        </tbody>
      </table>
    </div>
  )
}

function LinkCell({ url }: { url: string }) {
  const [copied, setCopied] = useState(false)
  const copy = async () => {
    try { await navigator.clipboard.writeText(url); setCopied(true); setTimeout(() => setCopied(false), 1500) } catch { /* ignore */ }
  }
  const short = url.replace(/^https?:\/\//, '').replace(/(anmalan\/.{8}).*/, '$1…')
  return (
    <div className="flex items-center gap-1.5">
      <code className="truncate text-xs text-ink-500" title={url}>{short}</code>
      <button onClick={copy} className="rounded p-1 text-ink-400 hover:bg-navy-50 hover:text-navy-800" title="Kopiera länk">{copied ? <Check className="size-3.5 text-success-500" /> : <Copy className="size-3.5" />}</button>
      <a href={url} target="_blank" rel="noreferrer" className="rounded p-1 text-ink-400 hover:bg-navy-50 hover:text-navy-800" title="Öppna anmälningssidan"><ExternalLink className="size-3.5" /></a>
    </div>
  )
}
